/**
 * Print-Only Wealth Gap Details Page for PDF Generation
 * Renders comprehensive wealth gap analysis for PDF export
 *
 * URL: /print/wealth-gap-details/:businessId
 */

import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import WealthGapPrintContent from '../../components/print/WealthGapPrintContent';
import { useDateFormatter } from '../../utils/dateUtils';
import '../../print.css';

const PrintWealthGapDetails = () => {
  const { businessId } = useParams();
  const { formatDate } = useDateFormatter();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState({
    businessProfile: null,
    wealthGap: null
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Get token from URL parameter or localStorage
        const urlParams = new URLSearchParams(window.location.search);
        const urlToken = urlParams.get('token');
        const token = urlToken || localStorage.getItem('token') || sessionStorage.getItem('token');

        const headers = {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-Business-Id': businessId
        };


        // Fetch data
        const [profileResponse, wealthGapResponse] = await Promise.all([
          fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/business/profile`, { headers }),
          fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/wealth-gap`, { headers })
        ]);

        const profileData = profileResponse.ok ? await profileResponse.json() : null;
        const wealthGapData = wealthGapResponse.ok ? await wealthGapResponse.json() : null;

        setData({
          businessProfile: profileData?.data?.profile || null,
          wealthGap: wealthGapData?.wealth_gap || null
        });

        setLoading(false);
      } catch (err) {
        console.error('[PrintWealthGapDetails] Error loading data:', err);
        setError(err.message);
        setLoading(false);
      }
    };

    fetchData();
  }, [businessId]);

  useEffect(() => {
    if (!loading && !error) {
      document.body.setAttribute('data-pdf-ready', 'true');

      // Set document title to business name
      document.title = `Wealth Gap Analysis Report`;
    }
  }, [loading, error]);

  if (loading) {
    return (
      <div className="print-container" data-pdf-loading="true">
        <div className="loading">Loading wealth gap details...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="print-container" data-pdf-error="true">
        <div className="error">Error: {error}</div>
      </div>
    );
  }

  const today = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  const businessName = data?.businessProfile?.business_name || 'Business Name Not Set';
  const industry = data?.businessProfile?.industry;

  return (
    <div className="print-container" data-pdf-ready="true">
      {/* Cover Page */}
      <div className="print-page cover-page">
        <div className="pdf-header">
          <img src="/dominion-header.png" alt="Dominion Business Advisors" />
        </div>
        <div className="cover-header">
          <h1>Wealth Gap Analysis Report</h1>
          <div className="report-type">Comprehensive Financial Exit Planning</div>
        </div>

        <div className="cover-business-info">
          <h2>{businessName}</h2>
          {industry && <p className="industry">{industry}</p>}
        </div>

        <div className="cover-footer">
          <p>Generated: {today}</p>
          <p className="confidential">Confidential Wealth Gap Analysis Report</p>
        </div>
      </div>

      {/* Content Pages - Custom structured content across pages 2-5 */}
      <WealthGapPrintContent wealthGap={data.wealthGap} />
    </div>
  );
};

export default PrintWealthGapDetails;
